import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, Calendar, Trophy } from 'lucide-react';
import { BannerAd } from '@/components/BannerAd';
import { TinyAd } from '@/components/TinyAd';
import fallbackImage from './images.png';
import fallbackBadge from './logo.png';


const FOTMOB_LEAGUE_ID = '289'; // AFCON 2025 Morocco

const MatchCard = ({ match }) => {
  const getStatusBadge = () => {
    if (match.status === 'live') {
      return (
        <div className="bg-red-600 text-white px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 animate-pulse">
          <span className="w-2 h-2 bg-white rounded-full"></span>
          LIVE{match.minute ? ` ${match.minute}'` : ''}
        </div>
      );
    }
    if (match.status === 'finished') {
      return (
        <div className="bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-xs font-semibold">
          FT
        </div>
      );
    }
    return null;
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  return (
    <Link
      to={`/match/${match.id}`}
      className="bg-gray-900/70 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden
                 hover:border-gray-600 transition-all duration-300 hover:shadow-xl hover:shadow-black/40
                 block h-full"
    >
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500 font-medium">{match.group || 'Group Stage'}</span>
          {getStatusBadge()}
        </div>

        <div className="flex items-center justify-between gap-3">
          {/* Home Team */}
          <div className="flex-1 text-center">
            {/* Image container */}
            <div className="w-20 h-20 mx-auto flex items-center justify-center mb-2">
              <img
                src={match.homeBadge}
                alt={match.homeTeam}
                className="max-w-full max-h-full object-contain"
                onError={(e) => (e.target.src = fallbackBadge)}
              />
            </div>

            {/* Team name */}
            <div className="font-medium text-sm sm:text-base text-white truncate">
              {match.homeTeam}
            </div>
          </div>

          {/* Score / VS */}
          <div className="text-center min-w-[80px]">
            {match.status === 'upcoming' ? (
              <div className="text-xl sm:text-2xl font-bold text-gray-400">VS</div>
            ) : (
              <div className="text-2xl sm:text-3xl font-black text-emerald-400 tracking-wider">
                {match.homeScore ?? '-'} - {match.awayScore ?? '-'}
              </div>
            )}
          </div>

          {/* Away Team */}
          <div className="flex-1 text-center">
            {/* Image container */}
            <div className="w-20 h-20 mx-auto flex items-center justify-center mb-2">
              <img
                src={match.awayBadge}
                alt={match.awayTeam}
                className="max-w-full max-h-full object-contain"
                onError={(e) => (e.target.src = fallbackBadge)}
              />
            </div>

            {/* Team name */}
            <div className="font-medium text-sm sm:text-base text-white truncate">
              {match.awayTeam}
            </div>
          </div>
        </div>
      </div>

      {/* Footer info */}
      <div className="bg-black/40 px-4 py-3 text-xs text-gray-400 flex flex-wrap gap-x-6 gap-y-1 border-t border-gray-800/70">
        <div className="flex items-center gap-1.5">
          <Clock size={14} />
          <span>{formatTime(match.kickoffTime)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <MapPin size={14} />
          <span className="truncate">{match.stadium || 'Morocco'}</span>
        </div>
      </div>
    </Link>
  );
};

const NewsCard = ({ article }) => (
  <a
    href={article.link}
    target="_blank"
    rel="noopener noreferrer"
    className="bg-gray-900/60 rounded-xl overflow-hidden border border-gray-800
               hover:border-gray-600 transition-all duration-300 hover:shadow-lg hover:shadow-black/30
               flex flex-col h-full"
  >
    <div className="relative aspect-[4/3] sm:aspect-[16/9] overflow-hidden">
      <img
        src={article.image || fallbackImage}
        alt={article.title}
        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        onError={(e) => (e.target.src = fallbackImage)}
      />
      <div className="absolute top-3 left-3">
        <span className="bg-emerald-600/90 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
          AFCON 2025
        </span>
      </div>
    </div>

    <div className="p-4 flex flex-col flex-grow">
      <h3 className="font-semibold text-base sm:text-lg text-white mb-2 line-clamp-2">
        {article.title}
      </h3>
      <p className="text-gray-400 text-sm line-clamp-3 mb-4 flex-grow">{article.excerpt}</p>

      <div className="flex items-center gap-2 text-xs text-gray-500 mt-auto">
        <Calendar size={14} />
        <time dateTime={article.date}>
          {new Date(article.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </time>
      </div>
    </div>
  </a>
);

const Countdown = ({ nextMatchTime }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (!nextMatchTime) return;

    const timer = setInterval(() => {
      const diff = new Date(nextMatchTime) - new Date();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [nextMatchTime]);

  if (!nextMatchTime) return null;

  return (
    <div className="bg-gradient-to-br from-emerald-950 to-green-950 rounded-2xl p-4 sm:p-8 border border-emerald-900/30 shadow-2xl">
      <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-4 sm:mb-6 flex items-center justify-center gap-3">
        <Trophy className="text-emerald-400" />
        Next Match Countdown
      </h2>

      {/* 📱 Mobile Timer: row with HH:MM:SS (always shown on mobile) */}
      <div className="sm:hidden flex justify-center gap-3 text-white text-2xl font-bold tracking-wide">
        <span>{timeLeft.days}d</span>
        <span>{timeLeft.hours.toString().padStart(2, '0')}h</span>
        <span>{timeLeft.minutes.toString().padStart(2, '0')}m</span>
        <span>{timeLeft.seconds.toString().padStart(2, '0')}s</span>
      </div>

      {/* 💻 Desktop Timer: grid of boxes (same as before) */}
      <div className="hidden sm:grid grid-cols-4 gap-3 sm:gap-4 max-w-lg mx-auto mt-4">
        {Object.entries(timeLeft).map(([unit, value]) => (
          <div
            key={unit}
            className="bg-black/30 backdrop-blur-sm rounded-xl p-3 sm:p-5 text-center border border-emerald-900/40"
          >
            <div className="text-2xl sm:text-4xl font-bold text-emerald-400 tracking-wider">
              {value.toString().padStart(2, '0')}
            </div>
            <div className="text-xs sm:text-sm text-emerald-200/80 uppercase mt-1 font-medium">
              {unit}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const HomePage = () => {
  const [matches, setMatches] = useState([]);
  const [nextMatchTime, setNextMatchTime] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matchesRes, newsRes] = await Promise.all([
          fetch(`https://www.fotmob.com/api/leagues?id=${FOTMOB_LEAGUE_ID}`),
          fetch('https://ingamefut-backend.vercel.app/api/news'),
        ]);

        let mappedMatches = [];

        if (matchesRes.ok) {
          const json = await matchesRes.json();

          let allMatches = [];
          // Very defensive – FotMob changes structure often
          if (Array.isArray(json.leagueOverviewMatches)) allMatches = json.leagueOverviewMatches;
          else if (Array.isArray(json.header?.matches)) allMatches = json.header.matches;
          else if (Array.isArray(json.overview?.matches)) allMatches = json.overview.matches;
          else if (Array.isArray(json.matches?.allMatches)) allMatches = json.matches.allMatches;
          else if (Array.isArray(json.fixtures?.allMatches)) allMatches = json.fixtures.allMatches;
          else if (Array.isArray(json.fixtures)) allMatches = json.fixtures;

          if (Array.isArray(allMatches) && allMatches.length > 0) {
            mappedMatches = allMatches.map(m => {
              let numericId = m.id;
              if (typeof numericId !== 'number' && !/^\d+$/.test(String(numericId))) {
                const url = m.pageUrl || m.matchPageUrl || '';
                const hashMatch = url.match(/#(\d+)$/);
                numericId = hashMatch ? hashMatch[1] : m.matchId || m.id || 'unknown';
              }

              return {
                id: numericId,
                group: m.tournamentStage || m.roundName || m.group || 'Group Stage',
                homeTeam: m.home?.name || 'Home Team',
                awayTeam: m.away?.name || 'Away Team',
                homeBadge: m.home?.id ? `https://images.fotmob.com/image_resources/logo/teamlogo/${m.home.id}.png` : 'https://via.placeholder.com/80',
                awayBadge: m.away?.id ? `https://images.fotmob.com/image_resources/logo/teamlogo/${m.away.id}.png` : 'https://via.placeholder.com/80',
                homeScore: m.status?.scoreStr ? m.status.scoreStr.split(' - ')[0]?.trim() : null,
                awayScore: m.status?.scoreStr ? m.status.scoreStr.split(' - ')[1]?.trim() : null,
                kickoffTime: m.status?.utcTime || m.date || m.time?.date || new Date().toISOString(),
                stadium: m.venue?.name || 'Morocco',
                status: m.status?.finished ? 'finished' : (m.status?.started ? 'live' : 'upcoming'),
                minute: m.status?.liveTime?.short || m.status?.elapsed || null,
              };
            });

            setMatches(mappedMatches);

            const upcoming = mappedMatches
              .filter(m => m.status === 'upcoming')
              .sort((a, b) => new Date(a.kickoffTime) - new Date(b.kickoffTime));

            if (upcoming.length > 0) setNextMatchTime(upcoming[0].kickoffTime);
          } else {
            console.warn('No valid matches array found in FotMob response');
          }
        }

        if (newsRes.ok) {
          const articles = await newsRes.json();
          setNews(articles);
        }
      } catch (err) {
        console.error('Data fetch error:', err);
      } finally {
        setLoading(false);
        setNewsLoading(false);
      }
    };

    fetchData();
  }, []);

  const liveMatches = matches.filter((m) => m.status === 'live');
  const upcomingMatches = matches.filter((m) => m.status === 'upcoming').slice(0, 4);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 to-black">
        <div className="text-emerald-400 text-xl sm:text-2xl font-medium animate-pulse">
          Loading AFCON 2025 live data...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-950 text-white pb-12">
      {/* Header + Countdown */}
      <div className="container mx-auto px-4 sm:px-6 pt-8 pb-12 md:pt-16">
        <div className="text-center mb-10 md:mb-16">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-400 mb-4">
            AFCON 2025 Morocco
          </h1>
          <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto">
            Real-time scores • Live updates • Full tournament coverage
          </p>
        </div>

        <div className="mb-12 md:mb-16">
          <Countdown nextMatchTime={nextMatchTime} />
        </div>
        <TinyAd />
        {/* Leaderboard Ad
        <div className="mb-12">
          <BannerAd type="leaderboard" />
        </div> */}
      </div>

      {/* LIVE MATCHES */}
      {liveMatches.length > 0 && (
        <section className="container mx-auto px-4 sm:px-6 mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 md:mb-8 flex items-center gap-3">
            <span className="text-red-500">●</span> Live Now
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 lg:gap-8">
            {liveMatches.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </div>
        </section>
      )}

      {/* UPCOMING */}
      <section className="container mx-auto px-4 sm:px-6 mb-16">
        <h2 className="text-2xl sm:text-3xl font-bold mb-6 md:mb-8">Upcoming Matches</h2>

        {upcomingMatches.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6 lg:gap-8">
            {upcomingMatches.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-gray-500 bg-gray-900/30 rounded-2xl border border-gray-800">
            No upcoming matches scheduled at the moment
          </div>
        )}
      </section>
      
      {/* In-feed Ad */}
      {/* <div className="container mx-auto px-4 sm:px-6 mb-16">
        <BannerAd type="infeed" />
      </div> */}
      {/* NEWS */}
      <section className="container mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold mb-6 md:mb-8">Latest News</h2>

        {newsLoading ? (
          <div className="text-center py-16 text-gray-500">Loading latest news...</div>
        ) : news.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-7 lg:gap-8">
            {news.slice(0, 8).map((article, i) => (
              <NewsCard key={i} article={article} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-gray-500 bg-gray-900/30 rounded-2xl border border-gray-800">
            No news available right now • Check back soon!
          </div>
        )}
      </section>
      <TinyAd />
      {/* <div className="block sm:hidden my-10">
        <BannerAd type="mobile" />
      </div> */}
      <div className="mt-16 text-center">
      </div>
    </div>
  );
};